import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { buildQuery, useApi } from './client'
import type { Currency, LicenseTariff } from './types'

const ROOT = '/license-tariffs'
const KEY = ['license-tariffs'] as const

type ListResp = { items: LicenseTariff[]; nextCursor: string | null }

export function useLicenseTariffs(opts: { withUsage?: boolean } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: [...KEY, opts],
    queryFn: () =>
      api<ListResp>(`${ROOT}${buildQuery({ with_usage: opts.withUsage ? 'true' : undefined })}`)
        .then((r) => r.items),
  })
}

export type CreateTariffInput = {
  bitrixIblockId: string
  name: string
  vendorCounterpartyId: string
  costPrice: number
  costCurrency: Currency
  allowOverpay?: boolean
}

export function useCreateLicenseTariff() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateTariffInput) =>
      api<LicenseTariff>(ROOT, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export type UpdateTariffInput = {
  id: string
  patch: Partial<{
    name: string
    vendorCounterpartyId: string
    costPrice: number
    costCurrency: Currency
    allowOverpay: boolean
  }>
}

export function useUpdateLicenseTariff() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: UpdateTariffInput) =>
      api<LicenseTariff>(`${ROOT}/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useDeleteLicenseTariff() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`${ROOT}/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
